import {
  Box,
  Container,
  Heading,
  Text,
  Stack,
  Grid,
  Icon,
  useBreakpointValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  UserPlus,
  FlagCheckered,
  Trophy,
  ChatCircleDots,
} from "phosphor-react";

const MotionBox = motion(Box);

interface StepProps {
  icon: React.ElementType;
  title: string;
  description: string;
  stepNumber: number;
}

const Step = ({ icon, title, description, stepNumber }: StepProps) => {
  const isDesktop = useBreakpointValue({ base: false, md: true });

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: stepNumber * 0.2 }}
    >
      <Stack
        spacing={4}
        p={6}
        height="100%"
        bg="oxygon.dark.200"
        rounded="xl"
        border="1px solid"
        borderColor="oxygon.dark.300"
        _hover={{
          borderColor: "oxygon.primary",
          transform: "translateY(-5px)",
        }}
        transition="all 0.3s"
        position="relative"
        data-aos="fade-up"
      >
        <Box position="relative">
          <Icon as={icon} w={10} h={10} color="oxygon.primary" />
          <Box
            position="absolute"
            top="-10px"
            right="-10px"
            bg="oxygon.secondary"
            color="white"
            w="24px"
            h="24px"
            rounded="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="sm"
            fontWeight="bold"
          >
            {stepNumber}
          </Box>
        </Box>
        <Stack spacing={2} flex={1}>
          <Heading size="md">{title}</Heading>
          <Text color="gray.400">{description}</Text>
        </Stack>
        {isDesktop && stepNumber < 4 && (
          <Box
            position="absolute"
            right="-50%"
            top="50%"
            transform="translateY(-50%)"
            width="100%"
            height="2px"
            bgGradient="linear(to-r, oxygon.primary, oxygon.secondary)"
            opacity={0.3}
            zIndex={-1}
          />
        )}
      </Stack>
    </MotionBox>
  );
};

const HowItWorks = () => {
  return (
    <Box py={20} bg="oxygon.dark.100">
      <Container maxW="container.xl">
        <Stack spacing={12}>
          <Stack textAlign="center" spacing={4} data-aos="fade-up">
            <Heading
              bgGradient="linear(to-r, oxygon.primary, oxygon.secondary)"
              bgClip="text"
            >
              Getting Started
            </Heading>
            <Text color="gray.400" maxW="800px" mx="auto">
              Create an account, track rounds, and compete with the simulator
              golf community.
            </Text>
          </Stack>

          <Grid
            templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }}
            gap={8}
            position="relative"
            alignItems="stretch"
          >
            <Step
              icon={UserPlus}
              title="Create a free account"
              description="Sign up in seconds with your email, then set up your profile and simulator details."
              stepNumber={1}
            />
            <Step
              icon={FlagCheckered}
              title="Play your first tracked round"
              description="Start a round, play on your sim, close it out with your score and optional media, and post it to your feed."
              stepNumber={2}
            />
            <Step
              icon={Trophy}
              title="Join tournaments, clubs, or challenges"
              description="Find events to join or create your own competitions to invite friends and members."
              stepNumber={3}
            />
            <Step
              icon={ChatCircleDots}
              title="Share & connect"
              description="Follow friends, comment on rounds, and stay connected to clubs, tours, and venues."
              stepNumber={4}
            />
          </Grid>
        </Stack>
      </Container>
    </Box>
  );
};

export default HowItWorks;
